import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Download,
  Edit3,
  Loader2,
  RefreshCw,
  Search,
  ThumbsUp,
  XCircle,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { useAppPermisos } from '../../hooks/useAppPermisos';
import { logModuloEvento } from '../../utils/bitacora';
import { useTableSort, sortRows, SortableTh } from '../../utils/tableSort';
import {
  btnPrimary,
  btnSecondary,
  inputCls,
  labelCls,
  pageSubtitleCls,
  pageTitleCls,
  selectCls,
  tableWrapCls,
  tdCls,
  thCls,
  theadCls,
  trCls,
} from '../../components/ui';

type EstadoJornada = 'pendiente' | 'aprobada' | 'rechazada' | 'corregida';

interface TrabajadorRow {
  id: string;
  boleta_id: string;
  labor_nombre: string | null;
  trabajador_codigo: string | null;
  trabajador_nombre: string;
  estado_asistencia: string;
  entrada_at: string | null;
  salida_at: string | null;
  horas_ordinarias: number | null;
  horas_extra: number | null;
  cantidad_produccion: number | null;
  tarifa_aplicada: number | null;
  monto_calculado: number | null;
  aprobado_planilla: boolean;
  observacion: string | null;
  estado_jornada?: EstadoJornada | null;
  jornada_ordinaria_horas?: number | null;
  revision_observacion?: string | null;
}

interface BoletaRow {
  id: string;
  numero: string;
  fecha: string;
  semana: string | null;
  supervisor_nombre: string | null;
  finca_nombre: string | null;
  lote_num: string | null;
  estadio_codigo: string | null;
  estado: string;
}

interface LaborRow {
  boleta_id: string;
  labor_nombre: string;
  modalidad: string;
  unidad: string;
}

interface BloqueRow {
  boleta_id: string;
  bloque_num: string | null;
}

interface JornadaView {
  trabajador: TrabajadorRow;
  boleta: BoletaRow | null;
  labor: LaborRow | null;
  bloques: string;
  estado: EstadoJornada;
  totalHoras: number;
  horasOrdSugeridas: number;
  horasExtSugeridas: number;
}

const ESTADO_LABEL: Record<EstadoJornada, string> = {
  pendiente: 'Pendiente',
  aprobada: 'Aprobada',
  rechazada: 'Rechazada',
  corregida: 'Corregida',
};

const ESTADO_TONE: Record<EstadoJornada, string> = {
  pendiente: 'var(--tone-info-text)',
  aprobada: 'var(--tone-success-text)',
  rechazada: '#f87171',
  corregida: 'var(--tone-warn-text)',
};

function today() {
  return new Date().toISOString().slice(0, 10);
}

function addDays(date: string, days: number) {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function weekLabel(fecha: string) {
  const d = new Date(`${fecha}T00:00:00`);
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${String(week).padStart(2, '0')}-${String(d.getFullYear()).slice(-2)}`;
}

function fmtDate(s?: string | null) {
  if (!s) return '-';
  return new Date(`${s}T00:00:00`).toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtTime(s?: string | null) {
  if (!s) return '-';
  return new Date(s).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function num(v?: number | null) {
  return Number(v ?? 0);
}

// Mismo recargo de horas extra que BoletasLaboresPage (tiempo y medio, CR).
const FACTOR_HORA_EXTRA = 1.5;
function montoPorHoras(ordinarias: number, extra: number, tarifa: number) {
  return ordinarias * tarifa + extra * tarifa * FACTOR_HORA_EXTRA;
}

function Badge({ estado }: { estado: EstadoJornada }) {
  return (
    <span
      className="inline-flex rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
      style={{ color: ESTADO_TONE[estado], background: 'var(--surface-overlay)', border: '1px solid var(--line)' }}
    >
      {ESTADO_LABEL[estado]}
    </span>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string | number; tone: string }) {
  return (
    <div className="rounded-lg border px-4 py-3" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
      <div className="mb-2 text-[11px] font-bold uppercase tracking-wider" style={{ color: tone }}>{label}</div>
      <div className="font-mono text-2xl font-bold" style={{ color: 'var(--ink)' }}>{value}</div>
    </div>
  );
}

function csvCell(value: unknown) {
  const s = String(value ?? '').replace(/"/g, '""');
  return `"${s}"`;
}

export function JornadasLaboresPage() {
  const empresaId = useEmpresaId();
  const [trabajadores, setTrabajadores] = useState<TrabajadorRow[]>([]);
  const [boletas, setBoletas] = useState<BoletaRow[]>([]);
  const [labores, setLabores] = useState<LaborRow[]>([]);
  const [bloques, setBloques] = useState<BloqueRow[]>([]);
  const [fechaDesde, setFechaDesde] = useState(addDays(today(), -7));
  const [fechaHasta, setFechaHasta] = useState(today());
  const [semana, setSemana] = useState('');
  const [boletaNo, setBoletaNo] = useState('');
  const [estado, setEstado] = useState<EstadoJornada | ''>('pendiente');
  const [q, setQ] = useState('');
  const [jornadaDiaria, setJornadaDiaria] = useState('8');
  const [editingId, setEditingId] = useState('');
  const [editOrd, setEditOrd] = useState('');
  const [editExt, setEditExt] = useState('');
  const [editUnidades, setEditUnidades] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const permisos = useAppPermisos();
  const sort = useTableSort<'trabajador_codigo' | 'trabajador_nombre' | 'boleta' | 'labor' | 'horas_ordinarias' | 'horas_extra' | 'cantidad_produccion' | 'estado'>({ key: 'trabajador_nombre', direction: 'asc' });

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    const { data: b, error: eB } = await supabase
      .from('campo_boletas')
      .select('id, numero, fecha, semana, supervisor_nombre, finca_nombre, lote_num, estadio_codigo, estado')
      .eq('empresa_id', empresaId)
      .gte('fecha', fechaDesde)
      .lte('fecha', fechaHasta)
      .order('fecha', { ascending: false });

    if (eB) {
      setError(eB.message);
      setBoletas([]);
      setTrabajadores([]);
      setLabores([]);
      setBloques([]);
      setLoading(false);
      return;
    }

    const boletaIds = (b ?? []).map((x) => x.id);
    if (boletaIds.length === 0) {
      setBoletas([]);
      setTrabajadores([]);
      setLabores([]);
      setBloques([]);
      setLoading(false);
      return;
    }

    const [tRes, lRes, blRes] = await Promise.all([
      supabase
        .from('campo_boleta_trabajadores')
        .select('id, boleta_id, labor_nombre, trabajador_codigo, trabajador_nombre, estado_asistencia, entrada_at, salida_at, horas_ordinarias, horas_extra, cantidad_produccion, tarifa_aplicada, monto_calculado, aprobado_planilla, observacion, estado_jornada, jornada_ordinaria_horas, revision_observacion')
        .eq('empresa_id', empresaId)
        .in('boleta_id', boletaIds)
        .order('trabajador_nombre'),
      supabase
        .from('campo_boleta_labores')
        .select('boleta_id, labor_nombre, modalidad, unidad')
        .eq('empresa_id', empresaId)
        .in('boleta_id', boletaIds),
      supabase
        .from('campo_boleta_bloques')
        .select('boleta_id, bloque_num')
        .eq('empresa_id', empresaId)
        .in('boleta_id', boletaIds),
    ]);

    if (tRes.error || lRes.error || blRes.error) {
      setError(tRes.error?.message || lRes.error?.message || blRes.error?.message || 'No se pudo cargar jornadas.');
    }
    setBoletas((b ?? []) as BoletaRow[]);
    setTrabajadores((tRes.data ?? []) as TrabajadorRow[]);
    setLabores((lRes.data ?? []) as LaborRow[]);
    setBloques((blRes.data ?? []) as BloqueRow[]);
    setLoading(false);
  }, [empresaId, fechaDesde, fechaHasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const rows = useMemo<JornadaView[]>(() => {
    const boletaMap = new Map(boletas.map((b) => [b.id, b]));
    const laborMap = new Map<string, LaborRow>();
    labores.forEach((l) => { if (!laborMap.has(l.boleta_id)) laborMap.set(l.boleta_id, l); });
    const bloquesMap = new Map<string, string[]>();
    bloques.forEach((b) => {
      if (!bloquesMap.has(b.boleta_id)) bloquesMap.set(b.boleta_id, []);
      if (b.bloque_num) bloquesMap.get(b.boleta_id)!.push(b.bloque_num);
    });
    const jornada = Math.max(0, Number(jornadaDiaria || 0));
    return trabajadores.map((t) => {
      const totalHoras = num(t.horas_ordinarias) + num(t.horas_extra);
      const estadoDerivado: EstadoJornada = t.estado_jornada || (t.aprobado_planilla ? 'aprobada' : 'pendiente');
      // La labor es por colaborador: usar la del trabajador; si no, la de la boleta (boletas viejas).
      const laborBoleta = laborMap.get(t.boleta_id) || null;
      const labor: LaborRow | null = t.labor_nombre
        ? { boleta_id: t.boleta_id, labor_nombre: t.labor_nombre, modalidad: laborBoleta?.modalidad || '', unidad: laborBoleta?.unidad || '' }
        : laborBoleta;
      return {
        trabajador: t,
        boleta: boletaMap.get(t.boleta_id) || null,
        labor,
        bloques: (bloquesMap.get(t.boleta_id) || []).join(', '),
        estado: estadoDerivado,
        totalHoras,
        horasOrdSugeridas: Math.min(totalHoras, jornada || totalHoras),
        horasExtSugeridas: jornada > 0 ? Math.max(0, totalHoras - jornada) : 0,
      };
    });
  }, [trabajadores, boletas, labores, bloques, jornadaDiaria]);

  const filtradas = useMemo(() => {
    const term = q.trim().toLowerCase();
    const sem = semana.trim();
    const bol = boletaNo.trim().toLowerCase();
    const base = rows.filter((r) => {
      if (estado && r.estado !== estado) return false;
      if (sem && (r.boleta?.semana || weekLabel(r.boleta?.fecha || today())) !== sem) return false;
      if (bol && !String(r.boleta?.numero || '').toLowerCase().includes(bol)) return false;
      if (!term) return true;
      return [
        r.trabajador.trabajador_codigo,
        r.trabajador.trabajador_nombre,
        r.labor?.labor_nombre,
        r.boleta?.numero,
        r.boleta?.lote_num,
        r.bloques,
        r.boleta?.supervisor_nombre,
      ].some((x) => String(x ?? '').toLowerCase().includes(term));
    });
    return sortRows(base, sort.value, (row, key) => {
      if (key === 'trabajador_codigo') return row.trabajador.trabajador_codigo;
      if (key === 'trabajador_nombre') return row.trabajador.trabajador_nombre;
      if (key === 'boleta') return row.boleta?.numero;
      if (key === 'labor') return row.labor?.labor_nombre;
      if (key === 'horas_ordinarias') return num(row.trabajador.horas_ordinarias);
      if (key === 'horas_extra') return num(row.trabajador.horas_extra);
      if (key === 'cantidad_produccion') return num(row.trabajador.cantidad_produccion);
      return row.estado;
    });
  }, [rows, q, semana, boletaNo, estado, sort.value]);

  const stats = useMemo(() => {
    const total = filtradas.length;
    const aprobadas = filtradas.filter((r) => r.estado === 'aprobada').length;
    const pendientes = filtradas.filter((r) => r.estado === 'pendiente' || r.estado === 'corregida').length;
    const horas = filtradas.reduce((s, r) => s + r.totalHoras, 0);
    return { total, aprobadas, pendientes, horas };
  }, [filtradas]);

  async function actualizarJornada(id: string, patch: Partial<TrabajadorRow>) {
    setSaving(id);
    const { error: e } = await supabase
      .from('campo_boleta_trabajadores')
      .update({ ...patch, revisado_at: new Date().toISOString() })
      .eq('id', id)
      .eq('empresa_id', empresaId);
    setSaving('');
    if (e) {
      setError(e.message);
      return;
    }
    setTrabajadores((prev) => prev.map((t) => t.id === id ? { ...t, ...patch, revisado_at: new Date().toISOString() } : t));
  }

  function startEdit(r: JornadaView) {
    setEditingId(r.trabajador.id);
    setEditOrd(String(num(r.trabajador.horas_ordinarias)));
    setEditExt(String(num(r.trabajador.horas_extra)));
    setEditUnidades(String(num(r.trabajador.cantidad_produccion)));
  }

  async function saveEdit(id: string) {
    const r = filtradas.find((x) => x.trabajador.id === id) || rows.find((x) => x.trabajador.id === id);
    const tarifa = num(r?.trabajador.tarifa_aplicada);
    const ord = Number(editOrd || 0);
    const ext = Number(editExt || 0);
    await actualizarJornada(id, {
      horas_ordinarias: ord,
      horas_extra: ext,
      cantidad_produccion: Number(editUnidades || 0),
      jornada_ordinaria_horas: Number(jornadaDiaria || 0),
      monto_calculado: tarifa > 0 ? montoPorHoras(ord, ext, tarifa) : r?.trabajador.monto_calculado,
      estado_jornada: 'corregida',
      aprobado_planilla: false,
    });
    logModuloEvento({
      empresaId,
      modulo: 'campo_labores',
      accion: 'corregir_jornada',
      entidad: 'campo_boleta_trabajadores',
      entidadId: id,
      descripcion: `Jornada de ${r?.trabajador.trabajador_nombre || id} corregida`,
    });
    setEditingId('');
  }

  async function aprobar(r: JornadaView) {
    await actualizarJornada(r.trabajador.id, {
      estado_jornada: 'aprobada',
      aprobado_planilla: true,
      jornada_ordinaria_horas: Number(jornadaDiaria || 0),
      revision_observacion: null,
    });
    logModuloEvento({
      empresaId,
      modulo: 'campo_labores',
      accion: 'aprobar_jornada',
      entidad: 'campo_boleta_trabajadores',
      entidadId: r.trabajador.id,
      descripcion: `Jornada de ${r.trabajador.trabajador_nombre} aprobada`,
    });
  }

  async function rechazar(r: JornadaView) {
    await actualizarJornada(r.trabajador.id, {
      estado_jornada: 'rechazada',
      aprobado_planilla: false,
      revision_observacion: 'Rechazada en revision de jornadas',
    });
    logModuloEvento({
      empresaId,
      modulo: 'campo_labores',
      accion: 'rechazar_jornada',
      entidad: 'campo_boleta_trabajadores',
      entidadId: r.trabajador.id,
      descripcion: `Jornada de ${r.trabajador.trabajador_nombre} rechazada`,
    });
  }

  async function aprobarFiltradas() {
    const ids = filtradas.filter((r) => r.estado !== 'aprobada' && r.estado !== 'rechazada').map((r) => r.trabajador.id);
    if (ids.length === 0) return;
    setSaving('bulk');
    const { error: e } = await supabase
      .from('campo_boleta_trabajadores')
      .update({
        estado_jornada: 'aprobada',
        aprobado_planilla: true,
        jornada_ordinaria_horas: Number(jornadaDiaria || 0),
        revisado_at: new Date().toISOString(),
      })
      .eq('empresa_id', empresaId)
      .in('id', ids);
    setSaving('');
    if (e) { setError(e.message); return; }
    logModuloEvento({
      empresaId,
      modulo: 'campo_labores',
      accion: 'aprobar_jornadas_filtradas',
      entidad: 'campo_boleta_trabajadores',
      descripcion: `${ids.length} jornada(s) aprobadas en lote`,
    });
    await cargar();
  }

  // Aprueba de una vez todas las jornadas pendientes/corregidas de UNA boleta
  // (por numero) y avanza campo_boletas.estado a 'aprobada' -- el camino
  // rapido cuando ya se reviso toda la boleta, sin tener que aprobar jornada
  // por jornada.
  async function aprobarBoletaCompleta() {
    const numero = boletaNo.trim();
    if (!numero) { setError('Indique el numero de boleta a aprobar.'); return; }
    const boleta = boletas.find((b) => b.numero.toLowerCase() === numero.toLowerCase());
    if (!boleta) { setError(`No se encontro la boleta ${numero} en el rango de fechas actual.`); return; }
    setSaving('boleta');
    setMensaje('');
    const { error: e } = await supabase.rpc('campo_boleta_aprobar_completa', {
      p_empresa_id: empresaId,
      p_boleta_id: boleta.id,
      p_jornada_ordinaria_horas: Number(jornadaDiaria || 0),
    });
    setSaving('');
    if (e) { setError(e.message); return; }
    logModuloEvento({
      empresaId,
      modulo: 'campo_labores',
      accion: 'aprobar_completa',
      entidad: 'campo_boletas',
      entidadId: boleta.id,
      descripcion: `Boleta ${boleta.numero} aprobada completa desde Jornadas`,
    });
    setMensaje(`Boleta ${boleta.numero} aprobada.`);
    await cargar();
  }

  function exportarCsv() {
    const headers = ['Empleado', 'Nombre Empleado', 'Boleta', 'Semana', 'Fecha', 'Labor', 'Inicial', 'Final', 'Hrs Ord', 'Hrs Ext', 'Unidades', 'Lote', 'Bloques', 'Supervisor', 'Estado'];
    const lines = filtradas.map((r) => [
      r.trabajador.trabajador_codigo,
      r.trabajador.trabajador_nombre,
      r.boleta?.numero,
      r.boleta?.semana || weekLabel(r.boleta?.fecha || today()),
      r.boleta?.fecha,
      r.labor?.labor_nombre,
      fmtTime(r.trabajador.entrada_at),
      fmtTime(r.trabajador.salida_at),
      num(r.trabajador.horas_ordinarias).toFixed(2),
      num(r.trabajador.horas_extra).toFixed(2),
      num(r.trabajador.cantidad_produccion).toFixed(2),
      r.boleta?.lote_num,
      r.bloques,
      r.boleta?.supervisor_nombre,
      ESTADO_LABEL[r.estado],
    ].map(csvCell).join(','));
    const blob = new Blob([[headers.map(csvCell).join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jornadas-${fechaDesde}-${fechaHasta}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="shrink-0 px-5 py-4" style={{ background: 'var(--surface-raised)', borderBottom: '1px solid var(--line)' }}>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h1 className={pageTitleCls}>Jornadas</h1>
            <p className={pageSubtitleCls}>Revision y aprobacion de horas laboradas antes del calculo de planilla</p>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:flex xl:items-end">
            <div>
              <label className={labelCls}>Desde</label>
              <input type="date" className={inputCls} value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Hasta</label>
              <input type="date" className={inputCls} value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Jornada diaria</label>
              <input type="number" step="0.25" className={inputCls} value={jornadaDiaria} onChange={(e) => setJornadaDiaria(e.target.value)} />
            </div>
            <button type="button" className={`${btnSecondary} flex items-center justify-center gap-2`} onClick={cargar} disabled={loading}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Recargar
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-5">
        {error && (
          <div className="mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm" style={{ background: 'var(--tone-warn-bg)', borderColor: 'var(--tone-warn-border)', color: 'var(--tone-warn-ink)' }}>
            <AlertTriangle size={17} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
            <span>{error}</span>
          </div>
        )}
        {mensaje && (
          <div className="mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm" style={{ background: 'var(--tone-success-bg)', borderColor: 'var(--tone-success-border)', color: 'var(--tone-success-ink)' }}>
            <CheckCircle2 size={17} style={{ color: 'var(--tone-success-text)', flexShrink: 0, marginTop: 1 }} />
            <span>{mensaje}</span>
          </div>
        )}

        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Kpi label="Jornadas" value={stats.total} tone="var(--emp-accent-txt)" />
          <Kpi label="Pendientes" value={stats.pendientes} tone="var(--tone-info-text)" />
          <Kpi label="Aprobadas" value={stats.aprobadas} tone="var(--tone-success-text)" />
          <Kpi label="Horas" value={stats.horas.toFixed(2)} tone="var(--tone-warn-text)" />
        </div>

        <div className="mb-4 rounded-lg border p-3" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
            <div>
              <label className={labelCls}>Semana No.</label>
              <input className={inputCls} value={semana} onChange={(e) => setSemana(e.target.value)} placeholder={weekLabel(today())} />
            </div>
            <div>
              <label className={labelCls}>Boleta No.</label>
              <div className="flex gap-1">
                <input className={inputCls} value={boletaNo} onChange={(e) => setBoletaNo(e.target.value)} placeholder="LC-00000001" />
                {permisos.perm_vob && (
                  <button type="button" className="rounded border px-2" style={{ borderColor: 'var(--line)', color: 'var(--tone-success-text)' }} disabled={saving === 'boleta' || !boletaNo.trim()} onClick={aprobarBoletaCompleta} title="Aprobar boleta completa">
                    {saving === 'boleta' ? <Loader2 size={14} className="animate-spin" /> : <ThumbsUp size={14} />}
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className={labelCls}>Estado</label>
              <select className={selectCls} value={estado} onChange={(e) => setEstado(e.target.value as EstadoJornada | '')}>
                <option value="">Todos</option>
                {Object.entries(ESTADO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Buscar</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-faint)' }} />
                <input className={`${inputCls} pl-8`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Empleado, labor, lote" />
              </div>
            </div>
            <div className="flex items-end gap-2">
              <button type="button" className={`${btnSecondary} flex flex-1 items-center justify-center gap-2`} onClick={exportarCsv} disabled={filtradas.length === 0}>
                <Download size={14} />
                Excel
              </button>
              {permisos.perm_vob && (
                <button type="button" className={`${btnPrimary} flex flex-1 items-center justify-center gap-2`} onClick={aprobarFiltradas} disabled={saving === 'bulk' || filtradas.length === 0}>
                  {saving === 'bulk' ? <Loader2 size={14} className="animate-spin" /> : <ClipboardCheck size={14} />}
                  Aprobar
                </button>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm" style={{ color: 'var(--ink-faint)' }}>
            <Loader2 size={18} className="animate-spin" />
            Cargando jornadas...
          </div>
        ) : filtradas.length === 0 ? (
          <div className="rounded-lg border border-dashed px-5 py-12 text-center" style={{ borderColor: 'var(--line)', color: 'var(--ink-faint)' }}>
            Sin jornadas en el filtro actual.
          </div>
        ) : (
          <div className={tableWrapCls}>
            <table className="w-full border-collapse text-xs">
              <thead className={theadCls}>
                <tr>
                  <th className={thCls}><SortableTh sort={sort} sortKey="trabajador_codigo">Empleado</SortableTh></th>
                  <th className={thCls}><SortableTh sort={sort} sortKey="trabajador_nombre">Nombre empleado</SortableTh></th>
                  <th className={thCls}><SortableTh sort={sort} sortKey="boleta">Boleta</SortableTh></th>
                  <th className={thCls}><SortableTh sort={sort} sortKey="labor">Labor</SortableTh></th>
                  <th className={thCls}>Inicial</th>
                  <th className={thCls}>Final</th>
                  <th className={`${thCls} text-right`}><SortableTh sort={sort} sortKey="horas_ordinarias" align="right">Hrs Ord</SortableTh></th>
                  <th className={`${thCls} text-right`}><SortableTh sort={sort} sortKey="horas_extra" align="right">Hrs Ext</SortableTh></th>
                  <th className={`${thCls} text-right`}><SortableTh sort={sort} sortKey="cantidad_produccion" align="right">Unidades</SortableTh></th>
                  <th className={thCls}>Lote</th>
                  <th className={thCls}>Bloques</th>
                  <th className={thCls}><SortableTh sort={sort} sortKey="estado">Estado</SortableTh></th>
                  <th className={`${thCls} text-right`}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map((r) => {
                  const editing = editingId === r.trabajador.id;
                  return (
                    <tr key={r.trabajador.id} className={trCls}>
                      <td className={`${tdCls} font-mono font-bold`} style={{ color: 'var(--emp-accent-txt)' }}>{r.trabajador.trabajador_codigo || '-'}</td>
                      <td className={tdCls}>
                        <div className="font-semibold" style={{ color: 'var(--ink)' }}>{r.trabajador.trabajador_nombre}</div>
                        <div style={{ color: 'var(--ink-faint)' }}>{r.trabajador.estado_asistencia}</div>
                      </td>
                      <td className={tdCls}>
                        <div className="font-mono">{r.boleta?.numero || '-'}</div>
                        <div style={{ color: 'var(--ink-faint)' }}>{fmtDate(r.boleta?.fecha)} / {r.boleta?.semana || weekLabel(r.boleta?.fecha || today())}</div>
                      </td>
                      <td className={tdCls}>{r.labor?.labor_nombre || '-'}</td>
                      <td className={tdCls}>{fmtTime(r.trabajador.entrada_at)}</td>
                      <td className={tdCls}>{fmtTime(r.trabajador.salida_at)}</td>
                      <td className={`${tdCls} text-right font-mono`}>
                        {editing ? <input className={`${inputCls} w-20 text-right`} value={editOrd} onChange={(e) => setEditOrd(e.target.value)} /> : num(r.trabajador.horas_ordinarias).toFixed(2)}
                      </td>
                      <td className={`${tdCls} text-right font-mono`}>
                        {editing ? <input className={`${inputCls} w-20 text-right`} value={editExt} onChange={(e) => setEditExt(e.target.value)} /> : num(r.trabajador.horas_extra).toFixed(2)}
                      </td>
                      <td className={`${tdCls} text-right font-mono`}>
                        {editing ? <input className={`${inputCls} w-20 text-right`} value={editUnidades} onChange={(e) => setEditUnidades(e.target.value)} /> : num(r.trabajador.cantidad_produccion).toFixed(2)}
                      </td>
                      <td className={tdCls}>{r.boleta?.lote_num || '-'}</td>
                      <td className={tdCls}>{r.bloques || '-'}</td>
                      <td className={tdCls}><Badge estado={r.estado} /></td>
                      <td className={`${tdCls} text-right`}>
                        <div className="flex justify-end gap-1">
                          {!permisos.perm_vob ? (
                            <span className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>Sin permiso</span>
                          ) : editing ? (
                            <>
                              <button type="button" className={btnPrimary} onClick={() => saveEdit(r.trabajador.id)} disabled={saving === r.trabajador.id}>Guardar</button>
                              <button type="button" className={btnSecondary} onClick={() => setEditingId('')}>Cancelar</button>
                            </>
                          ) : (
                            <>
                              <button type="button" className="rounded border p-2" style={{ borderColor: 'var(--line)', color: 'var(--ink-faint)' }} onClick={() => startEdit(r)} title="Corregir horas">
                                <Edit3 size={13} />
                              </button>
                              <button type="button" className="rounded border p-2" style={{ borderColor: 'var(--line)', color: 'var(--tone-success-text)' }} onClick={() => aprobar(r)} disabled={saving === r.trabajador.id || r.estado === 'aprobada'} title="Aprobar jornada">
                                {saving === r.trabajador.id ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                              </button>
                              <button type="button" className="rounded border p-2" style={{ borderColor: 'var(--line)', color: '#f87171' }} onClick={() => rechazar(r)} disabled={saving === r.trabajador.id || r.estado === 'rechazada'} title="Rechazar jornada">
                                <XCircle size={13} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default JornadasLaboresPage;
